import * as React from 'react';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { Box, Button, Paper, Typography, Checkbox, Drawer, Fab, Select, MenuItem, ListSubheader, ListItemText, FormControl, InputLabel } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { isEmpty } from 'underscore';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import IconButton from '@mui/material/IconButton';
import SaveIcon from '@mui/icons-material/Save';

import { useTranslation } from 'react-i18next';
import DateRangeSelector from '../../components/DateRangeSelector';
import KeySelector from './components/KeySelector';
import ConsumptionChart from './components/ConsumptionChart';
import { useSearchParams } from 'react-router-dom';
import RealizedConsumptionChart from './components/RealizedConsumptionChart';
import RealizedSavingsChart from './components/RealizedSavingsChart';
import EfficiencyChart from './components/EfficiencyChart';
import { ApiContext } from '../../App';
import SolarChart from './components/SolarChart';
import SenpilicTrendChartFactory from './components/SenpilicTrendsChartFactory';
import { useLocation } from 'react-router-dom';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function SenpilicTrends(props) {

    const {  facility } = props;
    const currencySymbol = props.clientInfo.currency_symbol;

    const { t } = useTranslation();

    var earlierDate = new Date();
    earlierDate.setDate(earlierDate.getDate() - 1);
    const [searchParams, setSearchParams] = useSearchParams();
    const [fromDate, setFromDate] = React.useState(earlierDate);
    const [toDate, setToDate] = React.useState(new Date());
    const [timeAggType, setTimeAggType] = React.useState("h");
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [data, setData] = React.useState({});
    const [realizedData, setRealizedData] = React.useState({});
    const [realizedSavingsData, setRealizedSavingsData] = React.useState({});
    const [efficiencyData, setEfficiencyData] = React.useState([]);
    const [cogenData, setCogenData] = React.useState({});
    const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
    const [graphTypes, setGraphTypes] = React.useState([])
    const {facilityApi, cogenPlanApi} = React.useContext(ApiContext)

    React.useEffect(() => {
        if (searchParams.get("from_date") && !isNaN(new Date(searchParams.get("from_date"))))
            setFromDate(new Date(searchParams.get("from_date")))
        if (searchParams.get("to_date") && !isNaN(new Date(searchParams.get("to_date"))))
            setToDate(new Date(searchParams.get("to_date")))
    },
    [searchParams])


    const defaultKeys = {
        indoor_temp: "average",
        set_point: "average",
        battery_level: false,
        power_meter: true,
        gas_meter: true,
        energy_price: true,
        consumption_money: false,
        consumption_kwh: false,
        outside_temp: true,
        carbon_intensity: false,
        on_off: true,
        fan_speed: true,
        mode: true,
        consumption_profile: true,
        realized: true,
        realized_savings: true,
        efficiency: true,
        electricity_consumption: true,
        cogeneration: true,
        solar_production: false,
        direct_irradiance: false,
        transformer: false,
    }

    const [selectedKeys, setSelectedKeys] = React.useState(defaultKeys);
    const [appliedKeys, setAppliedKeys] = React.useState(defaultKeys);

    const drawerInputWidth = 500;

    const [filters, setFilters] = React.useState({
        controller: [],
        name: [],
        system: [],
        location: [],
        tariff: []
    });

    const [selectedFilters, setSelectedFilters] = React.useState({
        controller: [],
        name: [],
        system: [],
        location: [],
        tariff: []
    });

    const [appliedFilters, setAppliedFilters] = React.useState(null); // we only call api to retrieve data when this isn't null


    const handleTimeAggTypeChange = (event, newValue) => {
        setTimeAggType(newValue);
    }

    const handleAPIError = () => {
        setAlertMessage(t("Problem fetching data."));
        setAlertOpen(true);
    }

    const {readingsApi} = React.useContext(ApiContext)

    React.useEffect(() => {

        setAppliedFilters(null);

        // hoping the previous state update completes before the one below. 
        // this guarantees rerender in the next hook upon facility change without calling trends endpoint until filters are fetched

        readingsApi.getTrendFilters(
            facility,
            filterData => {
                setFilters(filterData);

                facilityApi.retrieve(
                    facility,
                    facilityInfo => {

                        const savedAppliedFilters = facilityInfo.trends_applied_filters
                        const savedAppliedKeys = facilityInfo.trends_applied_keys

                        if (!isEmpty(savedAppliedFilters)) {
                            setSelectedFilters(savedAppliedFilters);
                            setAppliedFilters(savedAppliedFilters);
                        } else {
                            setSelectedFilters(filterData);
                            setAppliedFilters(filterData);
                        }

                        if (!isEmpty(savedAppliedKeys)) {
                            setSelectedKeys(savedAppliedKeys);
                            setAppliedKeys(savedAppliedKeys);
                        }

                    },
                    () => {
                        handleAPIError();
                    }
                )

            },
            () => {
                handleAPIError();
            }
        )
    },
        [facility]
    )

    React.useEffect(() => {
        if ((toDate - fromDate) >=  30*24*3600*1000 && timeAggType === 'h') {
            setTimeAggType('d')
        } else {
            if (appliedFilters) {
                readingsApi.getTrends(
                    facility,
                    fromDate,
                    toDate,
                    data => {
                        setData(data);
                        setGraphTypes([...new Set(data.data.map((series) => series.metadata.key))])
                    },
                    () => {
                        setData({});
                        handleAPIError();
                    },
                    timeAggType,
                    Object.fromEntries(
                        Object.entries(appliedKeys).filter(
                            ([k,v]) => !["consumption_profile","realized", "realized_savings", "efficiency"].includes(k)
                        ).map(([k,v]) => [k.toUpperCase(), v])
                    ),
                    appliedFilters?.controller.length !== filters.controller.length ? appliedFilters.controller : [],
                    appliedFilters?.location.length !== filters.location.length ? appliedFilters.location : [],
                    appliedFilters?.system.length !== filters.system.length ? appliedFilters.system : [],
                    appliedFilters?.name.length !== filters.name.length ? appliedFilters.name : [],
                    appliedFilters?.tariff.length !== filters.tariff.length ? appliedFilters.tariff : [],
                )
            }

            cogenPlanApi.getCoGenReport(
                facility, 
                fromDate, 
                toDate, 
                (data) => {
                    setCogenData(data)
                }, 
                () => {
                    setCogenData({});
                    handleAPIError();
                }, 
                timeAggType,
                true
            )

            cogenPlanApi.getRealizedSavingsReport(
                facility, 
                fromDate, 
                toDate, 
                (data) => {
                    setRealizedSavingsData(data)
                }, 
                () => {
                    setRealizedSavingsData({});
                    handleAPIError();
                }, 
                timeAggType
            )

            cogenPlanApi.getEfficiencyReport(
                facility, 
                fromDate, 
                toDate, 
                (data) => {
                    setEfficiencyData(data)
                }, 
                () => {
                    setEfficiencyData([]);
                    handleAPIError();
                }, 
                timeAggType
            )

            readingsApi.getTrends(
                facility,
                fromDate,
                toDate,
                data => {
                    setRealizedData(data);
                },
                () => {
                    setRealizedData({});
                    handleAPIError();
                },
                timeAggType,
                {
                    POWER_METER: true, 
                    ELECTRICITY_CONSUMPTION: true, 
                    COGENERATION: true, 
                    SOLAR_PRODUCTION: true, 
                    TRANSFORMER: true
                },
                [],
                [],
                [],
                [],
                [],
            )
        }

        
    },
        [facility, fromDate, toDate, timeAggType, appliedFilters, appliedKeys]
    )

    const closeFilterDrawer = () => {
        setFilterDrawerOpen(false);
        setAppliedFilters(selectedFilters);
    }

    const handleKeyChange = () => {
        setAppliedKeys(selectedKeys);
    }

    const handleSave = () => {
        facilityApi.saveTrendPreferences(
            facility,
            appliedKeys, appliedFilters,
            () => {
                window.alert(t("Preferences saved correctly."))
            },
            handleAPIError
        )
    }

    return (

        <Box
        >
            <Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ borderBottom: 2, borderColor: 'divider', p: 2, marginBottom: 3 }}>
                    <KeySelector
                        keys={selectedKeys}
                        onChange={(newKeys) => setSelectedKeys(newKeys)}
                        onClose={handleKeyChange}
                    />
                    <DateRangeSelector
                        fromDate={fromDate}
                        toDate={toDate}
                        onFromDateChange={(newDate) => setFromDate(newDate)}
                        onToDateChange={(newDate) => setToDate(newDate)}
                        small={true}
                    />
                    <ToggleButtonGroup
                        value={timeAggType}
                        exclusive
                        size="small"
                        onChange={handleTimeAggTypeChange}
                    >
                        <ToggleButton value="h">
                            {t("H")}
                        </ToggleButton>
                        <ToggleButton value="d">
                            {t("D")}
                        </ToggleButton>
                        <ToggleButton value="m">
                            {t("M")}
                        </ToggleButton>
                        <ToggleButton value="q">
                            {t("Q")}
                        </ToggleButton>
                        <ToggleButton value="y">
                            {t("Y")}
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button variant="contained" size="small" startIcon={<FilterAltIcon />} onClick={() => setFilterDrawerOpen(true)}>
                        {t("Filter")}
                    </Button>
                    <IconButton size="medium" color="primary" onClick={() => handleSave()}>
                        <SaveIcon />
                    </IconButton>
                </Stack>

                <Stack>
                    {
                        appliedKeys.consumption_profile && <ConsumptionChart data={cogenData} timeAggType={timeAggType} />
                    }
                    {
                        appliedKeys.realized && <RealizedConsumptionChart data={realizedData} timeAggType={timeAggType} />
                    }
                    {
                        appliedKeys.realized_savings && <RealizedSavingsChart data={realizedSavingsData} timeAggType={timeAggType} />
                    }
                    {
                        appliedKeys.solar_production &&
                            <SolarChart
                                data={data}
                                timeAggType={timeAggType}
                                currencySymbol={currencySymbol}
                                key={`graph-${t}`}
                            />
                    }    
                    <Stack> 
                        <SenpilicTrendChartFactory 
                            graphTypes={graphTypes}
                            data={data}
                            timeAggType={timeAggType}
                            currencySymbol={currencySymbol}
                            key={`graph-${t}`}
                            /> 
                    </Stack>
                    {
                        appliedKeys.efficiency && <EfficiencyChart data={efficiencyData} timeAggType={timeAggType} />
                    }    
                </Stack>
 
                
            </Stack>
            <Drawer anchor="right" open={filterDrawerOpen} onClose={() => closeFilterDrawer()}>
                <Stack spacing={3} padding={2}>
                    <Typography variant="h6" color="primary">{t("Filter")}</Typography>
                    <Autocomplete
                        multiple
                        limitTags={5}
                        value={selectedFilters.controller}
                        onChange={(event, newValue) => {
                            setSelectedFilters((val) => ({ ...val, controller: newValue }))
                        }}
                        options={filters.controller}
                        sx={{ width: drawerInputWidth }}
                        renderInput={(params) => <TextField {...params} label={t("Controllers")} />}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                {option}
                            </li>
                        )}
                    />
                    <Autocomplete
                        multiple
                        limitTags={5}
                        value={selectedFilters.location}
                        onChange={(event, newValue) => {
                            setSelectedFilters((val) => ({ ...val, location: newValue }))
                        }}
                        options={filters.location}
                        sx={{ width: drawerInputWidth }}
                        renderInput={(params) => <TextField {...params} label={t("Locations")} />}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                {option}
                            </li>
                        )}
                    />
                    <Autocomplete
                        multiple
                        limitTags={5}
                        value={selectedFilters.system}
                        onChange={(event, newValue) => {
                            setSelectedFilters((val) => ({ ...val, system: newValue }))
                        }}
                        options={filters.system}
                        sx={{ width: drawerInputWidth }}
                        renderInput={(params) => <TextField {...params} label={t("Systems")} />}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                {option}
                            </li>
                        )}
                    />
                    <Autocomplete
                        multiple
                        limitTags={5}
                        value={selectedFilters.name}
                        onChange={(event, newValue) => {
                            setSelectedFilters((val) => ({ ...val, name: newValue }))
                        }}
                        options={filters.name}
                        sx={{ width: drawerInputWidth }}
                        renderInput={(params) => <TextField {...params} label={t("Devices")} />}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                {option}
                            </li>
                        )}
                    />
                    <Autocomplete
                        multiple
                        limitTags={5}
                        value={selectedFilters.tariff}
                        onChange={(event, newValue) => {
                            setSelectedFilters((val) => ({ ...val, tariff: newValue }))
                        }}
                        options={filters.tariff}
                        sx={{ width: drawerInputWidth }}
                        renderInput={(params) => <TextField {...params} label={t("Tariffs")} />}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                {option}
                            </li>
                        )}
                    />
                </Stack>
            </Drawer>
            <Dialog
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Problem")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>

    )

};